import { FieldsWithOwnReadOnlyMaxWidth } from "@components/inputs/field/Field";
import { isNumericType } from "@evala/odata-metadata";
import { getBoundValue, getNestedValue, getNewItemsMaxId, getSharedContextPrefix } from "@odata/Data.utils";
import { FormattingTarget } from "@odata/OData.utils";
import {
    arrayMove,
    composeRefHandlers,
    forEachKey,
    getElementOuterHeight,
    getValue,
    isDefined,
    isNotDefined,
    isObjectEmpty,
    sortCompareFn
} from "@utils/general";
import { KeyboardShortcut } from "@utils/keyboardShortcutsManager/KeyboardShorcutsManager.utils";
import { memoizeOne } from "@utils/memoizeOne";
import { getTextWidthByElement, includesAccentsInsensitive } from "@utils/string";
import { getRectNormalized } from "@utils/zoom.utils";
import { cloneDeep, debounce } from "lodash";
import React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { VariableSizeList } from "react-window";

import { DASH_CHARACTER, INPUT_DEBOUNCE_TIME } from "../../../constants";
import { AppContext } from "../../../contexts/appContext/AppContext.types";
import { WithDomManipulator, withDomManipulator } from "../../../contexts/domManipulator/withDomManipulator";
import {
    FastEntryInputSizes,
    FormMode,
    GroupedField,
    IconSize,
    LabelStatus,
    Sort,
    TextAlign,
    ToggleState
} from "../../../enums";
import { TRecordAny, TRecordString } from "../../../global.types";
import { AuditTrailLineComparison, FieldAdditionalData, ModelEvent } from "../../../model/Model";
import BindingContext, { areBindingContextsDifferent, IEntity } from "../../../odata/BindingContext";
import TestIds from "../../../testIds";
import DateType from "../../../types/Date";
import NumberType from "../../../types/Number";
import KeyboardShortcutsManager from "../../../utils/keyboardShortcutsManager/KeyboardShortcutsManager";
import { FormStorage } from "../../../views/formView/FormStorage";
import ConfirmationButtons from "../../../views/table/ConfirmationButtons";
import { getConfirmationActionText } from "../../../views/table/TableView.render.utils";
import { TableWrapper } from "../../../views/table/TableView.styles";
import { VIEW_PADDING_VALUE } from "../../../views/View.styles";
import { Button, IconButton, IProps as IButtonProps } from "../../button";
import { FastEntry, FastEntryActionStatus, IFastEntryChild } from "../../fastEntry/FastEntry";
import { REORDER_ICON_MARGIN } from "../../fastEntry/FastEntry.styles";
import FastEntryList, { IFastEntryListChild, IFastEntryListChildRender } from "../../fastEntry/FastEntryList";
import { ButtonGroupStyled } from "../../formGroup";
import { CloseIcon } from "../../icon";
import { ICheckboxChange } from "../../inputs/checkbox";
import { ScrollBar } from "../../scrollBar";
import SimpleTable from "../../simpleTable";
import {
    ItemIndex,
    ORDER_BACKGROUND_RIGHT_MARGIN,
    ORDER_BACKGROUND_WIDTH,
    OrderBackground
} from "../../sortableList/SortableList.styles";
import { IColumn, IRow } from "../../table";
import {
    getInfoValue,
    IFieldDef,
    IFieldInfoProperties,
    IGetValueArgs,
    isRequired,
    isTGetValueFn,
    isVisible,
    TInfoValue,
    TInfoValueProp,
    UnavailableActionStyle
} from "../FieldInfo";
import SmartField from "../smartField";
import {
    ISmartFieldBlur,
    ISmartFieldChange,
    ISmartFieldHeightChange,
    ISmartFieldProps,
    ISmartFieldWidthChange
} from "../smartField/SmartField";
import { isFieldValueSame } from "../smartField/SmartFieldUtils";
import { IFormLineItemsDef } from "../smartFormGroup/SmartFormGroup";
import { getFormattedValueFromValues } from "../smartTable/SmartTable.utils";
import ErrorStepper from "./ErrorStepper";
import SearchField, { ISearchFieldProps, ISearchFieldState } from "./SearchField";
import {
    ButtonsFooter,
    CheckboxStyledMainToggle,
    ConfirmationButtonsWrapper,
    FastEntryLabelWrapper,
    GroupedFieldsNoWrap,
    SmartActionButtonStyled,
    StyledHeaderLabelAbs,
    StyledSmartFastEntryList
} from "./SmartFastEntryList.styles";
import { ActionType, addEmptyLineItem, ISmartFastEntriesActionEvent } from "./SmartFastEntryList.utils";

const VIRTUALIZATION_THRESHOLD = 20;

interface ISmartFastEntryRowsExtension {
    [id: number]: {
        isReadOnly?: boolean;
    };
}

interface ISmartFastEntryColumnsExtension {
    [id: string]: {
        width: string;
        widths: TRecordString;
        fieldWidth?: number;
        textAlign: TextAlign;

        // at least one of the field in the column IS read only
        hasReadOnly?: boolean;
        // at least one of the field in the column is NOT read only
        hasEditable?: boolean;

        isFirstReadOnly?: boolean;
    };
}

interface ISmartFastEntryLineExtension {
    [id: string]: {
        heights: TRecordString;
    };
}

export enum ActionButtonPosition {
    Before = "Before",
    After = "After"
}

type DefinitionProps =
    "columns"
    | "order"
    | "orderDirection"
    | "preserveOrderOnRemove"
    | "canReorder"
    | "isCollapsible"
    | "containsPrimary"
    | "isItemCloneable"
    | "isItemRemovable"
    | "isItemVisible"
    | "isItemSelectable"
    | "canAdd"
    | "isAddDisabled"
    | "isItemDisabled"
    | "isTheOnlyItemRemovable"
    | "unavailableActionStyle"
    | "isGroupToggleable"
    | "customActionButtons"
    | "disableFieldsOnActiveAction"
    | "lineItemActions"
    | "showLineNumbers";

export interface ICustomPreContentArgs<E = IEntity> {
    itemBc: BindingContext;
    index: number;
    item: E;
    items: E[];
    labelStatus?: LabelStatus;
}

export interface ISmartFastEntryListProps extends WithTranslation, WithDomManipulator, Pick<IFormLineItemsDef, DefinitionProps> {
    storage: FormStorage;
    groupId?: string;
    isReadOnly?: boolean;
    isDisabled?: TInfoValue<boolean>;
    isLight?: boolean;
    /** Never virtualize items */
    disableVirtualization?: TInfoValue<boolean>;
    // adds checkboxes and confirm buttons
    customAction?: { id: string; confirmLabel: string; ignoreTheme?: boolean; };
    /** Sometimes, e.g. when customPreContent is used for FastEntries, we move the items to the right by some amount.
     * This is ok when each item has its own labels. In case we use useLabelWrapping and items are not wrapped,
     * we need to move the main labels to the right by the same amount. labelsLeftMargin lets you pass that amount.*/
    labelsLeftMargin?: number;
    useLabelWrapping?: boolean; // should be boolean, not TInfoValue
    onCustomActionFinish?: () => void;
    /** List of properties from given entity (set) that are supposed to be shown */
    bindingContext: BindingContext;
    onBlur?: (args: ISmartFieldBlur) => Promise<void>;
    onChange?: (args: ISmartFieldChange) => void;
    onTemporalChange?: (args: ISmartFieldChange) => void;
    onAction?: (args: ISmartFastEntriesActionEvent) => void;
    isAddButtonTransparent?: boolean;

    renderAsFastEntryForReadOnly?: boolean;

    // should display draft changes for this list
    showChanges?: boolean;

    /** Renders custom content custom next to the "Add" button. */
    customFooterContent?: React.ReactElement;
    customPreContent?: (args: ICustomPreContentArgs) => React.ReactNode;
    /** Renders custom content just after the row / each fast entry row */
    customRowAfterContent?: (args: ICustomRowAfterContentArgs) => React.ReactNode;
    customAddButtonText?: string;
    addDisabledAlert?: IButtonProps["alert"];

    filterItems?: (items: IEntity[]) => IEntity[];
    style?: React.CSSProperties;
    className?: string;
    searchInChildProps?: string[];
    deletedItemsTranslationKey?: string;
    /** Pass isConfirmable to every field */
    isConfirmable?: boolean;
}

export interface ICustomRowAfterContentArgs<E = IEntity> extends
    Pick<ICustomPreContentArgs<E>, "itemBc" | "index" | "item">,
    Pick<ISmartFastEntryListProps, "onAction" | "onChange" | "onBlur" | "storage"> {
}

interface IState {
    columnsExtensions: ISmartFastEntryColumnsExtension;
    rowsExtensions: ISmartFastEntryRowsExtension;
    lineExtensions: ISmartFastEntryLineExtension;
    selectedItems: Record<number, boolean>;
    searchValue: string;
    isWrapped?: boolean;
    contentWidth?: number;
}

function getScrollParent(node: HTMLElement): HTMLElement {
    if (node == null) {
        return null;
    }

    if (node.scrollHeight > node.clientHeight) {
        return node;
    } else {
        return getScrollParent(node.parentElement);
    }
}


const ITEM_PROPS_DEFAULTS: Partial<Record<TInfoValueProp, { readOnly: boolean, editable: boolean }>> = {
    isItemDisabled: { readOnly: false, editable: false },
    isItemReadOnly: { readOnly: true, editable: false },
    isItemVisible: { readOnly: true, editable: true },
    isItemRemovable: { readOnly: false, editable: true },
    isItemCloneable: { readOnly: false, editable: true }
};

enum CalculationStateType {
    None,
    Items,
    Labels,
    Finished
}

class SmartFastEntryList extends React.Component<ISmartFastEntryListProps, IState> {
    static contextType = AppContext;

    _fastEntryListRef = React.createRef<HTMLDivElement>();
    _fastEntryRef = React.createRef<HTMLDivElement>();
    _buttonRef = React.createRef<HTMLButtonElement>();
    _errorStepperRef = React.createRef<ErrorStepper>();
    _virtualizedListRef = React.createRef<VariableSizeList>();
    _searchFieldRef = React.createRef<React.Component<ISearchFieldProps, ISearchFieldState>>();

    _calculationState = CalculationStateType.None;

    _updateAddPos = false;
    _focusItemN: number = null;

    _items: IEntity[];

    _calculatedBc: BindingContext;

    _lastVisibleColumnsIds: string[];


    // use debounce to prevent multiple slow re-renders
    handleSearchChange = debounce((searchValue: string) => {
        this.setState({
            searchValue
        });
    }, INPUT_DEBOUNCE_TIME);

    constructor(props: ISmartFastEntryListProps) {
        super(props);

        // automatically register on model
        this.props.storage.addRef(this, this.props.bindingContext);
        this.props.storage.emitter.on(ModelEvent.AfterLoad, this.handleFormAfterLoad);
        this.props.storage.emitter.on(ModelEvent.RecalculateFastEntryList, this.updateExtensions);

        const { columnsExtensions, rowsExtensions } = this.getExtensions();

        this.state = {
            // Set all possible (editable) column extensions before first render,
            // so that the field widths are immediately correct - needed when virtualization is used.
            columnsExtensions,
            rowsExtensions,
            lineExtensions: {},
            selectedItems: {},
            searchValue: ""
        };
    }

    componentDidMount() {
        if (this._calculationState === CalculationStateType.Labels) {
            this.updateLabelsPosition();
        }

        if (this._calculationState === CalculationStateType.Items && this.getItems().length > 0) {
            this.updateExtensions();
        }

        this._items = getBoundValue({
            bindingContext: this.props.bindingContext,
            data: this.props.storage.data.entity,
            dataBindingContext: this.props.storage.data.bindingContext
        });

        this.getListWidth();
    }

    componentWillUnmount() {
        this.props.storage.removeRef(this, this.props.bindingContext);
        this.props.storage.emitter.off(ModelEvent.AfterLoad, this.handleFormAfterLoad);
        this.props.storage.emitter.off(ModelEvent.RecalculateFastEntryList, this.updateExtensions);
    }

    componentDidUpdate(prevProps: ISmartFastEntryListProps, prevState: IState) {
        const bindingContextHasChanged = areBindingContextsDifferent(prevProps.bindingContext, this.props.bindingContext);
        if ((!prevProps.customAction && this.props.customAction) || bindingContextHasChanged) {
            this.setState({
                selectedItems: {}
            });
        }

        if (this.props.isReadOnly !== prevProps.isReadOnly) {
            this.updateExtensions();
            return;
        }
        
        const _currentItems = this.getItems();

        if ((this._calculationState === CalculationStateType.Items && _currentItems.length > 0) || bindingContextHasChanged) {
            this.updateExtensions();
            return;
        }

        if (this._calculationState === CalculationStateType.Finished) {
            const visibleColIds = this.getVisibleColumnIds();
            if (!visibleColIds.every(((vci, index) => vci === this._lastVisibleColumnsIds[index]))) {
                this.updateExtensions();
                this._lastVisibleColumnsIds = visibleColIds;
                return;
            }
        }

        // this is magic check when list if fully rendered and some EXTERNAL situation makes its items changed
        if (this._calculationState === CalculationStateType.Finished && this._items &&
            (this._items?.length !== _currentItems?.length ||
                this._items?.some((item, idx) => item !== _currentItems[idx]))) {
            this.updateExtensions();
        } else if (this._calculationState === CalculationStateType.Labels) {
            this.updateLabelsPosition();
        }

        if (!this.useVirtualization() && this.state.searchValue) {
            // when number of items gets under VIRTUALIZATION_THRESHOLD
            // remove filter value so that all items are rendered
            this.setState({
                searchValue: null
            });
        }

        this._items = _currentItems;

        // when add button is clicked, keep it in the same position for user by scrolling down the page , kinda hacky
        if (this._updateAddPos) {
            if (!this.useVirtualization() && this._fastEntryRef.current) {
                const diff = getElementOuterHeight(this._fastEntryRef.current?.parentElement?.parentElement);
                let scrollParent = getScrollParent(this._buttonRef.current);

                if (scrollParent) {
                    scrollParent = scrollParent.parentElement;
                }

                const scroll = getScrollParent(scrollParent);

                if (scroll && scroll.parentElement) {
                    scroll.parentElement.scrollTop += diff;
                }
            } else if (this.useVirtualization() && this._virtualizedListRef.current) {
                this._virtualizedListRef.current.scrollToItem(this.getItems().length, "start");
            }

            this._updateAddPos = false;
        }

        if (isDefined(this._focusItemN)) {
            const focusN = this._focusItemN;
            this._focusItemN = null;
            setTimeout(() => {
                this.focusFirstInputOfNItem(focusN);
            }, 20);
        }

        this.getListWidth();
    }

    getColumnBcWithItemKey = (columnId: string) => {
        // for some callback functions we need to pass binding context with item key, to work properly eg isVisible, isReadOnly, etc
        const bc = this.props.bindingContext;
        const item = this.getItems()?.[0];
        if (isDefined(item)) {
            return bc.addKey(item).navigate(columnId);
        }
        return bc.navigate(columnId);
    };

    getListWidth() {
        if (this._fastEntryListRef.current) {
            const width = this._fastEntryListRef.current.offsetWidth;
            if (this.state.contentWidth !== width) {
                this.setState({ contentWidth: width });
            }
        }
    }

    getVisibleColumnIds = () => {
        return this.props.columns?.filter(column => {
            const columnBindingContext = this.getColumnBcWithItemKey(column.id);
            const columnMetadata = this.props.storage.getInfo(columnBindingContext);
            if (!columnMetadata) {
                console.error(`Unable to load info for field ${column.id}`);
            }

            return getInfoValue(columnMetadata, "isVisible", {
                storage: this.props.storage,
                bindingContext: columnBindingContext,
                context: this.props.storage.context
            }) !== false;
        })?.map(col => col.id) ?? [];
    };

    /** Negative index can be used to select last item */
    focusFirstInputOfNItem = (index: number) => {
        if (index >= this.getItems().length || index < 0) {
            return;
        }

        // items can be virtualized - we cannot query all items, we need to query only the specific item which should be rendered
        const item = this._fastEntryListRef.current?.querySelector(`[data-fasteentry-id="${index}"]`);

        if (item) {
            const firstInput = item.querySelector("input,textarea");

            if (firstInput) {
                (firstInput as HTMLInputElement).focus();
            }
        }
    };


    handleFormAfterLoad = () => {
        this._calculationState = CalculationStateType.Items;
    };

    handleKeyDown = (event: React.KeyboardEvent): void => {
        if (KeyboardShortcutsManager.isEventShortcut(event, KeyboardShortcut.ALT_R)) {
            if (this.canAdd && !this.isAddDisabled) {
                this.handleAdd();
            }
        }
    };

    getCorrectIndexFromAttr = (item: HTMLDivElement) => {
        return parseFloat(item.dataset.fasteentryId);
    };

    getExtensions = () => {
        const columnsExtensions: ISmartFastEntryColumnsExtension = {};
        const currentColumnExtensions = this.state?.columnsExtensions;
        const currentRowExtensions = this.state?.rowsExtensions;
        let needRecalc = false;
        const invisibleCount = new Map<number, number>();
        const readOnlyCount = new Map<number, number>();
        // list of columns, at least on one rendered row is column visible;
        const _visibleColumns = new Set<string>();

        const SAFE_SPACE = 2; // safe space to prevent text cutting in rounding edge cases

        const { storage, bindingContext, columns } = this.props;

        this._calculatedBc = bindingContext;

        for (let colIdx = 0; colIdx < columns.length; colIdx++) {
            const column = columns[colIdx];
            const columnBindingContext = this.getColumnBcWithItemKey(column.id);
            const columnMetadata = storage.getInfo(columnBindingContext);
            if (!columnMetadata) {
                console.error(`Unable to load info for field ${column.id}`);
            }

            let infoWidth = getInfoValue(columnMetadata, "width", {
                bindingContext: columnBindingContext,
                info: columnMetadata,
                storage
            });
            const shouldFitContent = infoWidth === "fit-content";
            if (shouldFitContent) {
                infoWidth = null;
            }
            const fieldWidth = parseFloat(infoWidth ?? FastEntryInputSizes.M);

            let width = 0;
            let hasReadOnly, hasEditable, isFirstReadOnly;

            // if we are sure no field can be readonly (either there is no callback or "isReadOnly" is straight NO
            // we don't need to do any crazy width calculation from DOM and just set concrete value
            const canBeReadOnly = isTGetValueFn(columnMetadata.isReadOnly) || columnMetadata.isReadOnly === true || this.props.isReadOnly;

            if (!canBeReadOnly) {
                width = fieldWidth;
                hasEditable = true;
            }
            // top label, if the lines are not wrapped
            const mainLabel = this._fastEntryListRef.current?.querySelector(`[data-testid="${TestIds.FastEntryListHeader}"]`)?.querySelector(`[data-column-id="${column.id}"]`)?.querySelector(`[data-testid="${TestIds.FieldLabel}"]`) as HTMLDivElement;
            const htmlItems = this._fastEntryListRef.current?.querySelectorAll(`[data-testid="${TestIds.FastEntryItem}"]`);

            const hasOwnReadOnlyMaxWidth = FieldsWithOwnReadOnlyMaxWidth[columnMetadata.type] || shouldFitContent || columnMetadata.hasOwnReadOnlyMaxWidth;

            if (!htmlItems || htmlItems.length === 0) {
                needRecalc = true;
                width = 0;
            } else {
                const items = this.getItems();

                for (let rowIdx = 0; rowIdx < (htmlItems ?? []).length; rowIdx++) {
                    const htmlItem = htmlItems[rowIdx]; // rendered row

                    // for virtualization HTML div index doesn't match index of items
                    const correctIndex = this.getCorrectIndexFromAttr(htmlItem as HTMLDivElement);
                    const item = items[correctIndex];
                    const itemBc = bindingContext.addKey(item).navigate(column.id);

                    const isReadOnly = getInfoValue(columnMetadata, "isReadOnly", {
                        storage,
                        bindingContext: itemBc,
                        context: this.props.storage.context
                    }) || this.props.isReadOnly;

                    // read only columns should have width based on their widest row value
                    const isVisible = getInfoValue(columnMetadata, "isVisible", {
                        storage,
                        bindingContext: itemBc,
                        context: this.props.storage.context,
                        fastEntryListId: this.props.groupId
                    });

                    if (isReadOnly && isVisible !== false) {
                        let rowReadOnlyCount = readOnlyCount.get(correctIndex) ?? 0;
                        rowReadOnlyCount++;
                        readOnlyCount.set(correctIndex, rowReadOnlyCount);
                    }

                    let invisibleItemsInRow = invisibleCount.get(correctIndex) ?? 0;
                    if (isVisible === false) {
                        invisibleItemsInRow++;
                        invisibleCount.set(correctIndex, invisibleItemsInRow);
                        continue;
                    } else {
                        _visibleColumns.add(column.id);
                    }

                    const row = htmlItem.querySelector(`[data-testid="${TestIds.FormGroupLine}"]`);
                    const columnElement = row.querySelector(`[data-name="${columnBindingContext.getNavigationPath(true)}"]`);

                    isFirstReadOnly = isDefined(isFirstReadOnly) ? isFirstReadOnly : isReadOnly;

                    if (canBeReadOnly && isReadOnly && this.props.storage.formMode !== FormMode.AuditTrail) {
                        // todo: width for read only columns doesn't work in audit trail mode
                        // for maximum size, subtract paddings
                        const maxWidth = Math.min(fieldWidth, parseInt(FastEntryInputSizes.L) - 36);

                        const labelElement = mainLabel ?? columnElement?.querySelector(`[data-testid="${TestIds.FieldLabel}"]`) as HTMLDivElement;
                        let labelWidth = Math.max(getTextWidthByElement(labelElement?.textContent, labelElement), labelElement?.children[0] ? (labelElement.children[0] as HTMLDivElement).offsetWidth + SAFE_SPACE : 0);
                        if (isNaN(labelWidth)) {
                            console.warn(`Unable to calculate label width for ${column.id}, setting label width to 0`);
                            labelWidth = 0;
                        }
                        const textElement = columnElement?.querySelector(`[data-testid="${TestIds.Text}"]`) as HTMLDivElement;
                        const fieldContentElement = columnElement?.querySelector(`[data-testid="${TestIds.FieldContent}"]`) as HTMLDivElement;
                        const finalElement = (textElement?.children[0] ?? fieldContentElement?.children[0] ?? columnElement) as HTMLDivElement;
                        let elementWidth = finalElement?.offsetWidth;

                        if (finalElement === columnElement) {
                            console.warn("No text or field content element found, this might be probably mistake and width is not correctly calculated to the content size");
                        } else if (parseFloat(currentColumnExtensions[column.id]?.width) !== elementWidth) {
                            // increase only in case we calculate width of children element.
                            // It would increase the width of the whole column on each calculation otherwise
                            elementWidth += SAFE_SPACE;
                        }

                        width = Math.max(width, elementWidth, labelWidth);
                        if (!hasOwnReadOnlyMaxWidth) {
                            width = Math.min(maxWidth, width);
                        }
                        if (width < labelWidth) {
                            width = labelWidth;
                        }
                        hasReadOnly = true;
                    } else {
                        width = Math.max(width, fieldWidth);
                        hasEditable = true;
                    }
                }
            }

            columnsExtensions[column.id] = {
                width: `${width}px`,
                widths: {},
                fieldWidth,
                isFirstReadOnly,
                hasReadOnly, hasEditable,
                textAlign: columnMetadata.textAlign ?? (isNumericType(
                    columnBindingContext.getNavigationBindingContext(columnMetadata.fieldSettings?.displayName).getProperty()
                ) ? TextAlign.Right : TextAlign.Left)
            };
        }

        this._lastVisibleColumnsIds = _visibleColumns.values().toArray();

        const rowsExtensions: ISmartFastEntryRowsExtension = {};
        let hasReadOnlyRowInDisabledState = false;
        readOnlyCount.forEach((value, itemIndex) => {
            const invisibleRowCols = invisibleCount.get(itemIndex) ?? 0;
            const isWholeRowReadOnly = value && (invisibleRowCols + value === columns.length);
            rowsExtensions[itemIndex] = {
                isReadOnly: isWholeRowReadOnly
            };
            if (!isWholeRowReadOnly) {
                hasReadOnlyRowInDisabledState = true;
            }
            if (currentRowExtensions[itemIndex]?.isReadOnly !== isWholeRowReadOnly) {
                // need recalc after render, because fields will be rerendered in read-only state -> width will change
                needRecalc = true;
            }
        });

        this._calculationState = needRecalc ? CalculationStateType.Items : CalculationStateType.Labels;

        if (hasReadOnlyRowInDisabledState) {
            // in case there is a row, which is not fully read-only, it's rendered in disabled state
            // so we need to use defined field width as minimal width
            forEachKey(columnsExtensions, (columnId) => {
                const colExt = columnsExtensions[columnId];
                colExt.width = `${Math.max(parseFloat(colExt.width), colExt.fieldWidth)}px`;
                // if there is a row in disabled state, first column is automatically not read-only
                colExt.isFirstReadOnly = false;
                colExt.hasEditable = true;
            });
        }

        return { columnsExtensions, rowsExtensions };
    };

    correctLabelPosition = (data: IEntity, left: number, columnId: string) => {
        const bc = this.getColumnBcWithItemKey(columnId);
        const info = this.props.storage.getInfo(bc);
        const textAlign = data.textAlign;

        const _isRequired = isRequired({
            bindingContext: bc,
            info,
            storage: this.props.storage,
            fastEntryListId: this.props.groupId,
            isColumnInfo: true
        }) && data?.hasEditable;

        // UX shifts
        if ((_isRequired && textAlign === TextAlign.Left) ||
            (!_isRequired && textAlign === TextAlign.Left && !data.isFirstReadOnly)) {
            left += 12;
        }

        if (textAlign === TextAlign.Right && !data?.isFirstReadOnly) {
            left -= 12;
        }

        if (textAlign === TextAlign.Right && data?.isFirstReadOnly && data?.hasEditable) {
            left -= 8;
        }

        return left;
    };

    updateExtensions = () => {
        if (!this.renderAsTable) {
            const { columnsExtensions, rowsExtensions } = this.getExtensions();
            this.setState({
                columnsExtensions,
                rowsExtensions
            });
        }
    };

    updateLabelsPosition = () => {
        // we need to set calculationState to Finished, otherwise it won't recalculate width of items in next ticks
        this._calculationState = CalculationStateType.Finished;

        if (this.renderAsTable || !this.props.useLabelWrapping) {
            return;
        }

        const htmlItems = this._fastEntryListRef.current?.querySelectorAll(`[data-testid="${TestIds.FastEntryItem}"]`);
        if (!htmlItems || htmlItems.length === 0) {
            return;
        }

        const htmlLabelsWrapper = this._fastEntryListRef.current?.querySelector(`[data-testid="${TestIds.FastEntryListHeader}"]`);
        const htmlLabels = htmlLabelsWrapper?.children;

        if (!htmlLabelsWrapper || !htmlLabels || htmlLabels.length === 0) {
            return;
        }

        const htmlItem = htmlItems[0] as HTMLDivElement;
        const htmlRow = htmlItem.querySelector(`[data-testid="${TestIds.FormGroupLine}"]`) as HTMLDivElement;
        if (!htmlRow) {
            return;
        }

        const rowLeft = getRectNormalized(htmlRow, true).left;

        for (const htmlLabel of Array.from(htmlLabels) as HTMLDivElement[]) {
            const columnId = htmlLabel.dataset.columnId;

            const data = this.state.columnsExtensions[columnId];
            if (!data) {
                this.updateExtensions();
                continue;
            }

            const columnBindingContext = this.getColumnBcWithItemKey(columnId);
            const columnElement = htmlRow.querySelector(`[data-name="${columnBindingContext.getNavigationPath(true)}"]`) as HTMLDivElement;
            if (!columnElement) {
                continue;
            }

            const _fieldElement = columnElement.querySelector(`[data-testid="${TestIds.Field}"]`) as HTMLDivElement;
            const fieldElement = _fieldElement ?? columnElement;

            const paddingLeft = parseFloat(window.getComputedStyle(fieldElement).getPropertyValue("padding-left"));
            const hasBoth = data.hasEditable && data.hasReadOnly;
            const left = this.correctLabelPosition(data, getRectNormalized(columnElement, true).left - rowLeft + (!hasBoth ? paddingLeft : 0), columnId);
            htmlLabel.style.left = `${left}px`;
        }
    };

    isGroupedFieldStart = (groupedField?: GroupedField) => {
        return groupedField === GroupedField.MultiStart;
    };

    isGroupedFieldEnd = (groupedField?: GroupedField) => {
        return groupedField === GroupedField.MultiEnd;
    };

    getVisibleColumnsForLine = (lineBindingContext: BindingContext): Array<{
        column: IFieldDef;
        bindingContext: BindingContext;
        groupedField?: GroupedField;
    }> => {
        const { storage } = this.props;

        return this.props.columns.reduce((visibleColumns, column) => {
            const bindingContext = lineBindingContext.navigate(column.id);
            const info = storage.getInfo(bindingContext);
            const isVisible = getInfoValue(info, "isVisible", {
                storage,
                bindingContext,
                context: storage.context
            });

            if (isVisible !== false) {
                visibleColumns.push({
                    column,
                    bindingContext,
                    groupedField: getInfoValue(info, "groupedField", {
                        storage,
                        bindingContext,
                        context: storage.context
                    })
                });
            }

            return visibleColumns;
        }, [] as Array<{
            column: IFieldDef;
            bindingContext: BindingContext;
            groupedField?: GroupedField;
        }>);
    };

    renderFastEntryField = (args: {
        column: IFieldDef;
        bindingContext: BindingContext;
        auditTrailData: ReturnType<SmartFastEntryList["getAuditTrailData"]>;
        hasCustomAction: boolean;
        isSelectable: boolean;
        isRowReadOnly: boolean;
        heightPx?: string;
        labelStatus: LabelStatus;
        isSharpLeft: boolean;
        isSharpRight: boolean;
    }) => {
        const {
            column,
            bindingContext,
            auditTrailData,
            hasCustomAction,
            isSelectable,
            isRowReadOnly,
            heightPx,
            labelStatus,
            isSharpLeft,
            isSharpRight
        } = args;

        const { storage } = this.props;
        const colExtension = this._calculatedBc.getFullPath() === this.props.bindingContext.getFullPath() && this.state.columnsExtensions[column.id];
        if (!colExtension && this.props.useLabelWrapping) {
            // sometimes DOM manipulator doesn't trigger calculation, so we have to do it manually
            this._calculationState = CalculationStateType.Items;
        }

        const auditTrailLineType = auditTrailData?.lineData?.type;
        if (auditTrailLineType === AuditTrailLineComparison.AdditionalRow || auditTrailLineType === AuditTrailLineComparison.MissingRow) {
            // this renders fields without tabindex
            storage.setAdditionalFieldData(bindingContext, FieldAdditionalData.AuditTrailData, {}, false);
        }

        let width = colExtension?.width;
        if (colExtension?.hasEditable && colExtension?.hasReadOnly) {
            const columnBindingContext = this.props.bindingContext.navigate(column.id);
            const columnInfo = storage.getInfo(columnBindingContext);
            const isFieldReadOnly = getInfoValue(columnInfo, "isReadOnly", {
                storage,
                bindingContext,
                context: storage.context
            });

            // https://solitea-cz.atlassian.net/browse/DEV-18871
            // when different readonly/editable states are used in one column
            // we need to subtract width of the paddings (20+12) from the readonly fields
            // to make them the same size as the editable ones

            // isRowReadOnly - field is rendered as read-only just in case whole row is read-only
            if (isFieldReadOnly && isRowReadOnly) {
                width = `${parseInt(colExtension?.width) - 32}px`;
            }
        }

        const fieldDef: IFieldInfoProperties = {};
        if (hasCustomAction && (!isSelectable || this.props.disableFieldsOnActiveAction)) {
            fieldDef.isDisabled = true;
        }
        if (this.props.isConfirmable) {
            fieldDef.isConfirmable = true;
        }

        const props: ISmartFieldProps & { key: string } = {
            storage,
            fieldDef: isObjectEmpty(fieldDef) ? null : fieldDef,
            fastEntryListId: this.props.groupId,
            fieldProps: {
                width,
                textAlign: colExtension?.textAlign,
                isSharpRight,
                isSharpLeft,
                isLight: this.props.isLight,
                height: heightPx,
                isMultiLine: false,
                showChange: false,
                maxRows: 3,
                // Removed should be always passed down, otherwise let SmartField take its LabelStatus from fieldinfo
                labelStatus: labelStatus === LabelStatus.Removed ? labelStatus : undefined
            },

            useWidthWhenReadOnly: true,
            key: column.id,
            bindingContext,
            onBlur: this.handleBlur,
            onChange: this.handleChange,
            onTemporalChange: this.handleTemporalChange,
            onWidthChange: this.handleWidthChange,
            onHeightChange: this.handleHeightChange
        };

        if (this.props.isReadOnly) {
            props.fieldProps.isReadOnly = true;
        }
        if (!isRowReadOnly) {
            props.renderReadOnlyAsDisabled = true;
        }

        return (
            // todo should we disable all fields when isFormDisabled || isDisabled is true?
            <SmartField {...props} />
        );
    };

    getFastEntryFields = (args: {
        lineBindingContext: BindingContext;
        auditTrailData: ReturnType<SmartFastEntryList["getAuditTrailData"]>;
        hasCustomAction: boolean;
        isSelectable: boolean;
        isRowReadOnly: boolean;
        heightPx?: string;
        labelStatus: LabelStatus;
    }): Array<(args: IFastEntryChild) => React.ReactElement> => {
        const {
            lineBindingContext,
            auditTrailData,
            hasCustomAction,
            isSelectable,
            isRowReadOnly,
            heightPx,
            labelStatus
        } = args;

        const visibleColumns = this.getVisibleColumnsForLine(lineBindingContext);
        const fields: Array<(args: IFastEntryChild) => React.ReactElement> = [];
        let groupedFields: Array<(args: IFastEntryChild) => React.ReactElement> = [];
        let groupedFieldKey = 0;

        const pushSingleField = (column: IFieldDef, bindingContext: BindingContext, columnIndex: number) => {
            const isSharpLeft = columnIndex > 0;
            const isSharpRight = columnIndex < visibleColumns.length - 1;

            return (_args: IFastEntryChild) => this.renderFastEntryField({
                column,
                bindingContext,
                auditTrailData,
                hasCustomAction,
                isSelectable,
                isRowReadOnly,
                heightPx,
                labelStatus,
                isSharpLeft,
                isSharpRight
            });
        };

        const flushGroupedFields = (wrap: boolean) => {
            if (groupedFields.length === 0) {
                return;
            }

            const currentGroupedFields = groupedFields;
            groupedFields = [];

            if (!wrap) {
                fields.push(...currentGroupedFields);
                return;
            }

            fields.push((_args: IFastEntryChild) => {
                return (
                    <GroupedFieldsNoWrap
                        data-testid={TestIds.GroupedFields}
                        key={`groupedFields_${lineBindingContext.toString()}_${groupedFieldKey++}`}
                    >
                        {currentGroupedFields.map(renderField => renderField({ isSharpLeft: false, isSharpRight: false }))}
                    </GroupedFieldsNoWrap>
                );
            });
        };

        visibleColumns.forEach(({ column, bindingContext, groupedField }, columnIndex) => {
            const field = pushSingleField(column, bindingContext, columnIndex);

            if (this.isGroupedFieldStart(groupedField)) {
                flushGroupedFields(false);
                groupedFields = [field];
                return;
            }

            if (groupedFields.length > 0) {
                groupedFields.push(field);

                if (this.isGroupedFieldEnd(groupedField)) {
                    flushGroupedFields(true);
                }

                return;
            }

            fields.push(field);
        });

        flushGroupedFields(false);

        return fields;
    };

    getLineHeight = (lineBindingContext: BindingContext) => {
        let height;

        const lineExtension = this.state.lineExtensions[lineBindingContext.toString()];

        if (lineExtension) {
            height = Math.max(...Object.values(lineExtension.heights).map(colHeight => parseInt(colHeight)));
        }

        return height;
    };

    handleReorder = (dragIndex: number, hoverIndex: number) => {
        const allItems = this.getItems(false);

        if (hoverIndex - 1 >= allItems.length) {
            // sadly, it is now possible to drag items over/behind "removed" items indicators
            // ignore it, but ideally, prevent it completely somehow
            return;
        }

        const { visible, invisible } = allItems.reduce((ret, item) => {
            const type = this.isItem("isItemVisible", item, this.props.bindingContext.addKey(item)) ? "visible" : "invisible";
            ret[type].push(item);
            return ret;
        }, { visible: [], invisible: [] });

        let newItems = [...arrayMove(visible, dragIndex, hoverIndex), ...invisible];
        if (this.props.order) {
            newItems = newItems.map((item: IEntity, i: number) => {
                return { ...item, [this.props.order]: i + 1 }; // order property is expected from 1 not from 0
            });
        }

        this.props.onAction?.({
            bindingContext: this.props.bindingContext,
            actionType: ActionType.Reorder,
            groupId: this.props.groupId,
            items: newItems
        });
    };

    handleBlur = async (args: ISmartFieldBlur) => {
        // optimization - only recompute all errors if error state changed on blurred field
        const hadError = this.props.storage.getError(args.bindingContext);

        await this.props.onBlur?.(args);

        const hasError = this.props.storage.getError(args.bindingContext);

        if (hadError !== hasError) {
            this._errorStepperRef.current?.forceUpdate();
        }
    };

    _debounceForceUpdate = debounce(() => {
        this.forceUpdate();
    }, 300);

    handleChange = (e: ISmartFieldChange) => {
        e.groupId = this.props.groupId;
        this.showChanges && this._debounceForceUpdate();
        this.props.onChange?.(e);
    };

    handleTemporalChange = (e: ISmartFieldChange) => {
        e.groupId = this.props.groupId;
        this.props.onTemporalChange?.(e);
    };

    handleWidthChange = ({ width, bindingContext }: ISmartFieldWidthChange) => {
        const path = bindingContext.getPath();

        const currentWidth = this.state.columnsExtensions[path].width;
        const columnsExtensions = { ...this.state.columnsExtensions };

        columnsExtensions[path] = { ...columnsExtensions[path] };
        columnsExtensions[path].widths[bindingContext.toString()] = width;

        const maxWidth = Object.values(columnsExtensions[path].widths).reduce((maxWidth, currentWidth) => {
            return `${Math.max(parseInt(maxWidth), parseInt(currentWidth))}px`;
        }, "0");

        if (maxWidth !== currentWidth) {
            columnsExtensions[path].width = maxWidth;
            this.setState({
                columnsExtensions
            });
        }
    };

    handleHeightChange = ({ height, bindingContext }: ISmartFieldHeightChange) => {
        const linePath = getSharedContextPrefix(this.props.bindingContext, bindingContext).toString();
        const lineExtensions = {
            ...this.state.lineExtensions,
            [linePath]: {
                ...this.state.lineExtensions[linePath],
                heights: {
                    ...this.state.lineExtensions[linePath]?.heights,
                    [bindingContext.toString()]: height
                }
            }
        };

        this.setState({
            lineExtensions
        });
    };

    handleAdd = () => {
        if (this.props.isCollapsible) {
            const isExpanded = this.props.storage.getGroupStatus(this.props.groupId)?.isExpanded;
            if (!isExpanded) {
                this.props.storage.expandGroup(true, this.props.groupId);
                this.forceUpdate(this.addItem);
                return;
            }
        }

        if (this.state.searchValue) {
            // remove filter, otherwise new item will not be visible
            this.setState({
                searchValue: null
            });
            this._searchFieldRef.current?.setState({
                searchValue: null
            });
        }

        this._calculationState = CalculationStateType.Items;

        this.addItem();
    };

    getNewItemIndex = (): number => {
        return this.getItems(false).length;
    };

    addItem = (preventScrolling?: boolean) => {
        if (!preventScrolling) {
            this._updateAddPos = true;
            this._focusItemN = this.getNewItemIndex();
        }

        const items = this.getItems(false);
        const newItemsMax = getNewItemsMaxId(this.getItems(false, false, false));
        const newItem = addEmptyLineItem({
            storage: this.props.storage,
            bindingContext: this.props.bindingContext,
            newItemsCount: newItemsMax + 1,
            index: items.length + 1,
            order: this.props.order,
            columns: this.props.columns,
            context: this.props.storage.context
        });
        this.props.onAction({
            bindingContext: this.props.bindingContext,
            actionType: ActionType.Add,
            groupId: this.props.groupId,
            items: [...items, newItem],
            affectedItems: [newItem]
        });
    };

    handleClone = (clonedIndex: number) => {
        this._updateAddPos = true;
        this._focusItemN = this.getNewItemIndex();

        let newItems = [...this.getItems(false)];
        const visibleItems = [...this.getItems(true)];
        const originalItem = visibleItems[clonedIndex];
        const newItemsMax = getNewItemsMaxId(newItems);
        let newItem = BindingContext.createNewEntity(newItemsMax + 1);
        const keyName = this.props.bindingContext.getKeyPropertyName();

        for (const [key, value] of Object.entries(originalItem)) {
            if (key !== keyName && !(key in newItem)) {
                newItem[key] = cloneDeep(value);
            }
        }

        newItems.push(newItem);

        if (this.props.order) {
            newItems = newItems.map((item, index) => {
                return {
                    ...item,
                    [this.props.order]: index + 1
                };
            });
            // replace newItem, so it's the same object as in the list
            newItem = newItems.find(i => i[BindingContext.NEW_ENTITY_ID_PROP] === newItem[BindingContext.NEW_ENTITY_ID_PROP]);
        }

        // cloning should set dirty flag as we are cloning non-empty row and the new row is not saved on BE
        const itemBindingContext = this.props.bindingContext.addKey(newItem);
        this.props.storage.setDirty(itemBindingContext);

        this.props.onAction?.({
            bindingContext: this.props.bindingContext,
            items: newItems,
            groupId: this.props.groupId,
            actionType: ActionType.Clone,
            affectedItems: [newItem]
        });
    };

    handleRemove = (removedIndex: number) => {
        // Note: there may be difference between visible items and all items. We need to work with both
        const visibleItems = this.getItems();
        let items = this.getItems(false);

        this._focusItemN = removedIndex >= items.length - 1 ? removedIndex - 1 : removedIndex;

        const removedItem = visibleItems[removedIndex];
        const itemBindingContext = this.props.bindingContext.addKey(removedItem);

        // remove width from columnsExtensions for removed bindingContext
        for (const column of Object.keys(this.state.columnsExtensions)) {
            const colBc = itemBindingContext.navigate(column);
            if (this.state.columnsExtensions[column].widths[colBc.getFullPath()]) {
                this.handleWidthChange({ width: "0px", bindingContext: colBc });
            }
        }

        // remove extension for the line
        const lineExtensions = { ...this.state.lineExtensions };
        delete lineExtensions[itemBindingContext.getFullPath()];

        this.setState({
            lineExtensions
        });

        items = items.filter(item => item !== removedItem);

        if (this.props.order && !this.props.preserveOrderOnRemove) {
            // order property is expected from 1 not from 0
            const _getNextOrder = (idx: number) => this.props.orderDirection === Sort.Desc ? items.length - idx : idx + 1;

            items = items.map((item, i) => { // update Order
                return { ...item, [this.props.order]: _getNextOrder(i) };
            });
        }

        this._calculationState = CalculationStateType.Items;

        this.props.onAction?.({
            bindingContext: this.props.bindingContext,
            actionType: ActionType.Remove,
            groupId: this.props.groupId,
            items,
            affectedItems: [removedItem]
        });
    };

    handleFocusPreviousItem = (index: number): void => {
        this.focusFirstInputOfNItem(index - 1);
    };

    handleFocusNextItem = (index: number): void => {
        this.focusFirstInputOfNItem(index + 1);
    };

    handleReorderUp = (index: number): void => {
        if (index > 0) {
            const newIndex = index - 1;

            this._focusItemN = newIndex;
            this.handleReorder(index, newIndex);
        }
    };

    handleReorderDown = (index: number): void => {
        if (index < this.getItems().length - 1) {
            const newIndex = index + 1;

            this._focusItemN = newIndex;
            this.handleReorder(index, newIndex);
        }
    };

    sortByOrder = (a: IEntity, b: IEntity) => {
        const dir = this.props.orderDirection === Sort.Desc ? Sort.Desc : Sort.Asc;

        return sortCompareFn(a[this.props.order], b[this.props.order], dir);
    };

    useVirtualization = (applySearchValue = false): boolean => {
        const { storage, bindingContext } = this.props;
        const items = this.getItems(true, applySearchValue);
        const disableVirtualization = getInfoValue(this.props, "disableVirtualization", {
            storage, items, bindingContext
        });
        return isNotDefined(this.props.customRowAfterContent) && !disableVirtualization && items.length > VIRTUALIZATION_THRESHOLD;
    };

    handleWrapChange = (isWrapped: boolean) => {
        if (!isWrapped) {
            this._calculationState = CalculationStateType.Labels;
        }
        this.setState((state: IState) => ({ ...state, isWrapped }));
    };

    getLabels = () => {
        return this.props.columns
            .map((column) => {
                const bc = this.getColumnBcWithItemKey(column.id);
                const data = this.state.columnsExtensions?.[column.id];
                const width = !data?.width || data.width === "0px" ? "auto" : data.width;

                const info = this.props.storage.getInfo(bc);
                const textAlign = data?.textAlign;

                const _isVisible = isVisible({
                    bindingContext: bc,
                    info,
                    storage: this.props.storage,
                    fastEntryListId: this.props.groupId,
                    isColumnInfo: true
                });

                const _isLabelRemoved = info.labelStatus === LabelStatus.Removed;

                if (!_isVisible || _isLabelRemoved) {
                    return null;
                }

                const _isRequired = isRequired({
                    bindingContext: bc,
                    info,
                    storage: this.props.storage,
                    fastEntryListId: this.props.groupId,
                    isColumnInfo: true
                }) && data?.hasEditable;

                const key = bc.toString();

                return <FastEntryLabelWrapper data-column-id={column.id} key={key}>
                    <StyledHeaderLabelAbs
                        data-column-id={column.id}
                        width={width}
                        textAlign={textAlign}
                        isRequired={_isRequired}
                        isLight={this.props.isLight}
                        isHidden={info.labelStatus === LabelStatus.Hidden}
                        hasPadding={false}
                        key={key}
                        _opacity={this._calculationState === CalculationStateType.Items ? 0 : 1}
                    >{info.label}
                    </StyledHeaderLabelAbs>;
                </FastEntryLabelWrapper>;
            })
            .filter(label => !!label); // filter out invisible fields' labels
    };

    getAuditTrailData = (bc: BindingContext) => {
        if ((this.props.storage as FormStorage)?.formMode === FormMode.AuditTrail) {
            return this.props.storage.getAdditionalFieldData(bc, FieldAdditionalData.AuditTrailData);
        }

        return undefined;
    };

    isFormDisabled = () => {
        return this.props.storage?.isDisabled;
    };

    isComparison = () => {
        return (this.props.storage as FormStorage)?.formMode === FormMode.AuditTrail;
    };

    isReadOnlyFormMode = () => {
        return (this.props.storage as FormStorage)?.isReadOnly;
    };

    /**
     * returns if whole line item is removable or cloneable - when in readOnly mode,
     * item is by default not cloneable or removable, however in default form, all line items
     * are by default removable and cloneable, so we handle here different default value.
     */
    isItem = (name: TInfoValueProp, item: IEntity, bindingContext: BindingContext) => {
        const propVal = getInfoValue(this.props, name, {
            storage: this.props.storage,
            bindingContext, item,
            context: this.props.storage.context,
            fastEntryListId: this.props.groupId
        });
        const isReadOnlyMode = this.isReadOnlyFormMode();
        const defaults = ITEM_PROPS_DEFAULTS[name];
        const defaultValue = (isReadOnlyMode ? defaults?.readOnly : defaults?.editable) ?? false;

        return isDefined(propVal) ? propVal : defaultValue;
    };

    handleCollapsed = () => {
        const isExpanded = this.props.storage.getGroupStatus(this.props.groupId)?.isExpanded;
        this.props.storage.expandGroup(!isExpanded, this.props.groupId);
        this.forceUpdate();
    };

    handleCollapsingEnd = () => {
        this.props.storage.emitter.emit(ModelEvent.RecalculateScrollbar);
    };

    getMainToggleState = (): ToggleState => {
        let allToggled = true;
        let allUntoggled = true;

        const items = this.getItems();

        for (let i = 0; i < items.length; i++) {
            const isSelectable = this.isSelectable(items[i]);

            allToggled = allToggled && (this.state.selectedItems[i] || !isSelectable);
            allUntoggled = allUntoggled && (!this.state.selectedItems[i] || !isSelectable);
        }

        if (allToggled) {
            return ToggleState.AllChecked;
        } else if (allUntoggled) {
            return ToggleState.AllUnchecked;
        } else {
            return ToggleState.Other;
        }
    };

    isSelectable(item: IEntity, lineBindingContext?: BindingContext) {
        if (!lineBindingContext) {
            lineBindingContext = this.props.bindingContext.addKey(item);
        }
        return lineBindingContext.isNew() ? false : this.isItem("isItemSelectable", item, lineBindingContext);
    }

    handleSelect = (id: number, selected: boolean) => {
        this.setState({
            selectedItems: {
                ...this.state.selectedItems,
                [id]: selected
            }
        });
    };

    handleMainToggleChange = (e: ICheckboxChange) => {
        const items = this.getItems();
        const selectedItems: Record<number, boolean> = {};

        for (let i = 0; i < items.length; i++) {
            const isSelectable = this.isSelectable(items[i]);

            if (isSelectable) {
                selectedItems[i] = e.value;
            }
        }

        this.setState({
            selectedItems
        });
    };

    getSelectedItems = () => {
        return this.getItems()
            .filter((item, index) => !!this.state.selectedItems[index]);
    };

    handleCustomActionConfirm = () => {
        const items = this.getSelectedItems();

        // custom action sends only selected items in 'items'
        this.props.onAction({
            bindingContext: this.props.bindingContext,
            actionType: ActionType.Custom,
            customActionType: this.props.customAction.id,
            groupId: this.props.groupId,
            items, // todo: refactor customActions, use affectedItems instead of items
            affectedItems: items
        });

        this.props.onCustomActionFinish?.();
    };

    getItems = (onlyVisible = true, applySearchValue?: boolean, applyFilter = true): IEntity[] => {
        if (applySearchValue === undefined) {
            applySearchValue = onlyVisible;
        }

        // clone items to prevent side effect mutation on the original object (like sort)
        let _items: IEntity[] = [...getBoundValue({
            bindingContext: this.props.bindingContext,
            data: this.props.storage.data.entity,
            dataBindingContext: this.props.storage.data.bindingContext
        }) || []];

        if (onlyVisible) {
            _items = _items.filter((item: IEntity) => this.isItem("isItemVisible", item, this.props.bindingContext.addKey(item)));
        }

        if (applyFilter && this.props.filterItems) {
            _items = this.props.filterItems(_items);
        }

        if (this.props.order) {
            _items.sort(this.sortByOrder);
        }

        // todo how exactly should filtering work?
        if (this.state?.searchValue && applySearchValue) {
            _items = _items.filter(item => {
                let values = Object.values(item);
                if (this.props.searchInChildProps) {
                    for (const prop of this.props.searchInChildProps) {
                        values = [
                            ...values,
                            ...Object.values(item[prop])
                        ];
                    }
                }
                const searchValue = this.state.searchValue;

                return values.some(val => {
                    let correctedSearchVal = searchValue;
                    let correctedVal;

                    if (typeof val === "number") {
                        const parsedSearchVal = NumberType.parse(searchValue);

                        if (isNaN(val) || isNaN(parsedSearchVal)) {
                            return false;
                        }

                        correctedVal = Math.abs(val).toString();
                        correctedSearchVal = parsedSearchVal.toString();
                    } else if (val && val instanceof Date) {
                        correctedVal = DateType.format(val);
                    } else {
                        correctedVal = val;
                    }

                    return includesAccentsInsensitive(correctedVal?.toString(), correctedSearchVal);
                });
            });
        }

        return _items;
    };

    get showChanges() {
        return isDefined(this.props.showChanges) ? this.props.showChanges : this.props.storage.data.definition.showChanges;
    }

    getRemovedItems = (): IEntity[] => {
        if (!this.showChanges) {
            return [];
        }

        const items = this.getItems(true, false);
        let origItems: IEntity[] = getBoundValue({
            bindingContext: this.props.bindingContext,
            data: this.props.storage.data.origEntity,
            dataBindingContext: this.props.storage.data.bindingContext
        }) || [];

        const keyName = this.props.bindingContext.getKeyPropertyName();

        if (this.props.filterItems) {
            origItems = this.props.filterItems(origItems);
        }

        return origItems.filter(origItem => !items.find(item => item[keyName] === origItem[keyName]));
    };

    renderMainToggle = () => {
        return (
            <CheckboxStyledMainToggle checked={this.getMainToggleState() === ToggleState.AllChecked}
                                      onChange={this.handleMainToggleChange}/>
        );
    };

    handleCustomButtonAction = (id: string): void => {
        const items = this.getSelectedItems();

        this.props.onAction({
            bindingContext: this.props.bindingContext,
            actionType: ActionType.Custom,
            customActionType: id,
            groupId: this.props.groupId,
            items,
            affectedItems: items
        });
    };

    renderCustomActionButtons = (position: ActionButtonPosition) => {
        const visibleActions = this.props.customActionButtons.filter(action => {
            const _isVisible = isVisible({ info: action, storage: this.props.storage });
            return _isVisible && (action.position === position || (!action.position && position === ActionButtonPosition.After));
        });

        if (visibleActions.length === 0) {
            return null;
        }

        return (
            <ButtonGroupStyled align={TextAlign.Left}>
                {visibleActions.map(action => (
                    <SmartActionButtonStyled isBefore={position === ActionButtonPosition.Before} key={action.id}
                                             storage={this.props.storage} action={action}
                                             onClick={this.handleCustomButtonAction}/>))}
            </ButtonGroupStyled>
        );
    };

    renderConfirmationButtons = () => {
        const selectedCount = Object.values(this.state.selectedItems).reduce((count, isSelected) => {
            return count + (isSelected ? 1 : 0);
        }, 0);

        return (
            <ConfirmationButtonsWrapper>
                <ConfirmationButtons isLight
                                     isTransparent
                                     ignoreTheme={this.props.customAction.ignoreTheme}
                                     onCancel={this.props.onCustomActionFinish}
                                     onConfirm={this.handleCustomActionConfirm}
                                     isDisabled={selectedCount === 0}
                                     confirmText={getConfirmationActionText(this.props.customAction.confirmLabel, selectedCount)}/>
            </ConfirmationButtonsWrapper>
        );
    };

    handleCustomLineItemAction = (customActionType: string, itemIndex: number): void => {
        const visibleItems = this.getItems();
        const item = visibleItems[itemIndex];

        this.props.onAction({
            bindingContext: this.props.bindingContext.addKey(item),
            actionType: ActionType.Custom,
            customActionType,
            groupId: this.props.groupId,
            items: [item],
            affectedItems: [item]
        });
    };

    renderReadOnly = (sortedItems: IEntity[]) => {
        const columns = this.props.columns.filter(col => {
            const colBc = this.getColumnBcWithItemKey(col.id);
            const isVisible = getInfoValue(this.props.storage.getInfo(colBc), "isVisible", {
                storage: this.props.storage,
                bindingContext: colBc,
                context: this.props.storage.context
            });
            return isVisible !== false;
        }).map(col => {
            const colBc = this.props.bindingContext.navigate(col.id);
            const colId = colBc.getNavigationPath();
            const colDef: IFieldInfoProperties = this.props.storage.data.mergedDefinition?.[colId]?.fieldDef ?? {};
            const colInfo = this.props.storage.getInfo(colBc);
            const colExt = this.state.columnsExtensions[col.id];
            const path = this.props.bindingContext.getPath(true);

            // width and columns in line item definition is different from table columns width and has to be removed
            // additionalProperties use root notation like 'id: "/Items/UnitPrice"'
            // we use different root here than in FormView load => remove the /Items prefix, if present
            const additionalProperties = colDef.additionalProperties?.map(def => {
                const prefix = `/${path}`;
                if (def.id.startsWith(prefix)) {
                    return {
                        ...def,
                        id: def.id.slice(prefix.length)
                    };
                }

                return def;
            });

            return {
                ...colDef, ...col,
                bindingContext: colBc,
                additionalProperties,
                label: col.label ?? (colInfo?.label || ""),
                textAlign: colDef?.textAlign ?? colExt?.textAlign,
                width: null, columns: null
            } as IFieldDef;
        });

        const removableRows: { id: string, index: number }[] = [];
        const isReadOnlyForm = this.props.storage.isReadOnly;
        const rows: IRow[] = sortedItems.map((item: IEntity, idx: number) => {
            const lineBindingContext = this.props.bindingContext.addKey(item);
            const values: IEntity = {};
            for (const column of columns) {
                // Formatters are usually made to work with regular fields.
                // Unfortunately, we try to use them from multiple places, with multiple different bc/item/entity values.
                // => To make them work from here, if the column has a formatter, we have to call it the same way SmartField would.
                // For other columns, use getFormattedValueFromValues to format values automatically, like in SmartTable.
                const colHasFormatter = !!column.formatter;
                const bindingContext = lineBindingContext.navigate(column.id);
                const info = {
                    ...column,
                    bindingContext
                };

                if (colHasFormatter) {
                    const value = getNestedValue(column.id, item);
                    const formattedValue = column.formatter(value, {
                        entity: this.props.storage.data.entity,
                        item: item,
                        bindingContext,
                        readonly: true,
                        target: FormattingTarget.Table,
                        storage: this.props.storage
                    });

                    if (column.render) {
                        values[column.id] = column.render({
                            storage: this.props.storage,
                            bindingContext,
                            props: {
                                formattedValue,
                                isReadOnly: true,
                                info
                            }
                        });
                    } else {
                        values[column.id] = formattedValue;
                    }
                }

                // it seems like some values are not necessarily handled by the formatter
                // as it is expected for Selects that the value is already stored in their (additional) items
                // => call getFormattedValueFromValues not only if colHasFormatter is false, but even if "formatter" didn't return any data
                if (!colHasFormatter || isNotDefined(values[column.id])) {
                    values[column.id] = getFormattedValueFromValues({
                        column: info,
                        values: item,
                        valuesBindingContext: this.props.bindingContext,
                        storage: this.props.storage
                    });
                }

                if (isNotDefined(values[column.id]) || values[column.id] === "") {
                    values[column.id] = DASH_CHARACTER;
                }
            }

            const rowId = `row-${idx}`;

            if (!isReadOnlyForm && this.isItem("isItemRemovable", item, lineBindingContext)) {
                removableRows.push({
                    id: rowId,
                    index: idx
                });
            }

            return {
                id: rowId,
                values,
                isDisabled: this.isItem("isItemDisabled", item, lineBindingContext)
            };
        });
        const getContentAfter = (row: IRow, index: number) => {
            const removableRow = removableRows.find(r => r.id === row.id);

            if (!removableRow) {
                return null;
            }

            return (
                <IconButton title={this.props.t("Common:General.Remove")}
                            onClick={() => this.handleRemove(index)}
                            isDecorative
                            isTransparent>
                    <CloseIcon width={IconSize.S} height={IconSize.S}/>
                </IconButton>
            );
        };


        const rowHeight = 30;
        if (!rows.length) {
            return null;
        }
        return (
            <>
                <TableWrapper hasMinHeight={false}>
                    {this.props.showLineNumbers &&
                        <OrderBackground
                            style={{
                                top: `${rowHeight}px`,
                                height: `${rows.length * rowHeight}px`,
                                left: `-${VIEW_PADDING_VALUE}px`
                            }}
                        >
                            {rows.map((row, index) => {
                                return (
                                    <ItemIndex key={index}
                                               $height={`${rowHeight}px`}
                                               $top={"8px"}>
                                        {index + 1}
                                    </ItemIndex>
                                );
                            })}
                        </OrderBackground>}
                    <ScrollBar
                        style={{
                            overflowX: "visible",
                            width: "100%",
                            height: "auto"
                        }}
                    >
                        <SimpleTable columns={columns as unknown as IColumn[]}
                                     rows={rows}
                                     contentAfter={getContentAfter}
                                     hasBigFont
                                     showHeaderBorder={false}/>
                    </ScrollBar>
                </TableWrapper>
                {this.props.customFooterContent ?
                    <div style={{ marginTop: "20px" }}>{this.props.customFooterContent}</div> : null}
            </>
        );
    };

    handleErrorFocusChange = (errorBc: BindingContext, itemIndex: number) => {
        this._virtualizedListRef.current.scrollToItem(itemIndex, "start");
    };

    renderErrorStepper = () => {
        return <ErrorStepper storage={this.props.storage}
                             bindingContext={this.props.bindingContext}
                             groupId={this.props.groupId}
                             isLight={this.props.isLight}
                             ref={this._errorStepperRef}
                             onErrorFocusChange={this.handleErrorFocusChange}/>;
    };

    getCustomPreContent = (labelStatus: LabelStatus, index: number): React.ReactNode => {
        const items = this.getItems();
        const item = items[index];
        const lineBindingContext = this.props.bindingContext.addKey(item);

        return this.props.customPreContent({
            itemBc: lineBindingContext,
            index,
            item,
            items,
            labelStatus
        });
    };

    getCustomPreContentWithLabelStatus = memoizeOne((labelStatus: LabelStatus) => {
        return this.getCustomPreContent.bind(this, labelStatus);
    });

    getFastEntryListChildren = (sortedItems: IEntity[], isFormDisabled: boolean): IFastEntryListChild[] => {
        const { storage } = this.props;
        const children: IFastEntryListChild[] = [];
        const removedItems = this.getRemovedItems();

        const canReorder = this.canReorder;

        const originalOrder = storage.data.origEntity?.Items?.reduce((origOrderMap: TRecordAny, item: IEntity) => {
            origOrderMap[item[this.props.bindingContext.getKeyPropertyName()]] = item[this.props.order];
            return origOrderMap;
        }, {}) ?? {};

        // use getItems to get all items, not just filtered ones (when virtualized)
        // so that only LAST item can not be removed
        const disableRemoveAction = !this.canRemoveLastItem && this.getItems(false, false).length === 1;

        sortedItems.forEach((row, index) => {
            const lineBindingContext = this.props.bindingContext.addKey(row);
            const height = this.getLineHeight(lineBindingContext);
            const key = lineBindingContext.toString();
            let heightPx: string;

            if (height) {
                heightPx = `${height}px`;
            }

            children.push({
                id: index.toString(),
                showIndicator: () => {
                    const isAnyFieldChanged = this.props.columns.some(column => {
                        const bc = lineBindingContext.navigate(column.id);
                        const info = storage.getInfo(bc);
                        const isVisible = getInfoValue(info, "isVisible", {
                            storage,
                            bindingContext: bc,
                            context: storage.context
                        });

                        return isVisible !== false && !isFieldValueSame(storage, bc);
                    });
                    const showNewLineIndicator = !this.props.bindingContext.getRootParent()?.isNew() &&
                        !storage.loading && (lineBindingContext.isNew() || isAnyFieldChanged || row[this.props.order] !== originalOrder[lineBindingContext.getKey()])
                        && this.showChanges && !this.getAuditTrailData(lineBindingContext);

                    return showNewLineIndicator;
                },
                render: ({
                             dragHandleProps,
                             connectEntryRef,
                             labelStatus
                         }: IFastEntryListChildRender) => {
                    const auditTrailData = this.getAuditTrailData(lineBindingContext);
                    const isDisabled = this.isItem("isItemDisabled", row, lineBindingContext);
                    const itemCannotBeDeleted = !!storage.getBackendDisabledFieldMetadata(lineBindingContext)?.cannotBeDeleted;
                    const hasCustomAction = !!this.props.customAction;
                    const isSelectable = this.isSelectable(row, lineBindingContext);
                    const isRowReadOnly = this.state.rowsExtensions[index]?.isReadOnly;
                    const cannotRemoveItem = !this.isItem("isItemRemovable", row, lineBindingContext);
                    const hideRemoveAction = isRowReadOnly ? true : this.props.unavailableActionStyle !== UnavailableActionStyle.Disable;
                    const rowFields = this.getFastEntryFields({
                        lineBindingContext,
                        auditTrailData,
                        hasCustomAction,
                        isSelectable,
                        isRowReadOnly,
                        heightPx,
                        labelStatus
                    });

                    return (
                        <React.Fragment key={key}>
                            <FastEntry index={index}
                                       auditTrailData={auditTrailData}
                                       hideCloneAction={hasCustomAction || !this.isItem("isItemCloneable", row, lineBindingContext) || !this.canAdd}
                                       hideRemoveAction={hasCustomAction || (cannotRemoveItem && hideRemoveAction)}
                                       disableCloneAction={this.isDisabled || !this.canAdd}
                                       disableRemoveAction={this.isDisabled || itemCannotBeDeleted || disableRemoveAction || (cannotRemoveItem && !hideRemoveAction)}
                                       actionStatus={this.useVirtualization() ? FastEntryActionStatus.Hidden : FastEntryActionStatus.Default}
                                       onClone={this.handleClone}
                                       onCustomAction={this.handleCustomLineItemAction}
                                       onRemove={this.handleRemove}
                                       onFocusPreviousItem={this.handleFocusPreviousItem}
                                       onFocusNextItem={this.handleFocusNextItem}
                                       onReorderUp={!hasCustomAction && canReorder ? this.handleReorderUp : null}
                                       onReorderDown={!hasCustomAction && canReorder ? this.handleReorderDown : null}
                                       canReorder={!hasCustomAction && canReorder}
                                       isReorderDisabled={storage.isDisabled}
                                       isSelectable={hasCustomAction && isSelectable}
                                       isSelected={this.state.selectedItems[index]}
                                       onSelect={this.handleSelect}
                                       lineItemActions={this.props.lineItemActions}
                                       isDisabled={isFormDisabled || isDisabled}
                                       dragHandleProps={dragHandleProps}
                                       customPreContent={this.props.customPreContent ? this.getCustomPreContentWithLabelStatus(labelStatus) : null}
                                // emulate the size of the checkbox for unselectable item
                                       style={{ marginLeft: hasCustomAction && !isSelectable ? "38px" : null }}
                                       className={labelStatus !== LabelStatus.Removed ? "visible-label" : null}
                                /* we need some ref to be set, but when virtualized, we don't know which one is rendered, so last rendered ref wins and is assigned to the refObject */
                                       passRef={composeRefHandlers(this._fastEntryRef, connectEntryRef)}>
                                {rowFields}
                            </FastEntry>
                            {this.props.customRowAfterContent?.({
                                storage,
                                itemBc: lineBindingContext,
                                item: row,
                                index,
                                onBlur: this.handleBlur,
                                onChange: this.handleChange,
                                onAction: this.props.onAction,
                            })}
                        </React.Fragment>
                    );
                }
            });
        });

        if (removedItems.length > 0) {
            const tKey = this.props.deletedItemsTranslationKey ?? "Components:SmartFastEntryList.ItemsDeleted";
            children.push({
                id: (sortedItems.length + 1).toString(),
                render: null,
                showIndicator: () => {
                    return true;
                },
                isEmptyItem: true,
                emptyItemText: this.props.t(tKey, { count: removedItems.length })
            });
        }

        return children;
    };

    get canReorder() {
        return getValue(this.props.canReorder, {
            storage: this.props.storage,
            context: this.props.storage.context
        }) && !this.isDisabled && !this.props.isReadOnly;
    }

    get canRemoveLastItem() {
        return this.props.isTheOnlyItemRemovable;
    }

    get showOrderIcon() {
        return this.canReorder && !this.isComparison();
    }

    get canAdd(): boolean {
        if (this.props.isReadOnly) {
            return false;
        }

        if (this.props.isGroupToggleable) {
            const items = this.getItems(false, false);
            if (!items.length) {
                // toggleable group without items should not show add button
                return false;
            }
        }

        const args: IGetValueArgs = {
            storage: this.props.storage,
            context: this.props.storage.context,
            bindingContext: this.props.bindingContext
        };

        return getValue(this.props.canAdd, args);
    }

    get isAddDisabled(): boolean {
        const args = {
            storage: this.props.storage,
            context: this.props.storage.context
        };

        return getValue(this.props.isAddDisabled, args) || this.isDisabled;
    }

    /** In the context of SmartFastEntryList, isDisabled means that the items of the collection cannot be add or removed.
     * We don't care about disabled state of the fields, that is handled by SmartField for each field */
    get isDisabled(): boolean {
        return getInfoValue(this.props, "isDisabled", {
            storage: this.props.storage,
            bindingContext: this.props.bindingContext,
            context: this.props.storage.context
        }) || !!this.props.storage.getBackendDisabledFieldMetadata(this.props.bindingContext);
    }

    get renderAsTable(): boolean {
        return this.props.isReadOnly && !this.props.renderAsFastEntryForReadOnly && isNotDefined(this.props.customRowAfterContent);
    }

    render = () => {
        const sortedItems = this.getItems();

        if (this.props.isGroupToggleable && !sortedItems?.length) {
            return null;
        }

        if (this.renderAsTable) {
            return this.renderReadOnly(sortedItems);
        }

        const isFormDisabled = this.isFormDisabled();
        const canAdd = this.canAdd;
        const useVirtualization = this.useVirtualization();

        let labelsLeftPad = 0;

        if (!!this.props.customAction) {
            // todo this is suspicious, maybe the value should be passed from place it is used in
            labelsLeftPad = 42;
        } else {
            if (this.showOrderIcon) {
                // icon + its margin
                labelsLeftPad += IconSize.asNumber("M") + REORDER_ICON_MARGIN;
            }
            if (this.props.showLineNumbers) {
                labelsLeftPad += ORDER_BACKGROUND_WIDTH + ORDER_BACKGROUND_RIGHT_MARGIN;
            }
        }

        if (this.props.labelsLeftMargin) {
            labelsLeftPad += this.props.labelsLeftMargin;
        }

        /* Currently only VAT table is using customFooterContent. The VAT table should have same
            width as FastEntryList content in case it is not wrapped, but the other content (buttons)
            should be on the most right edge -> set the width only to the CustomFooterContent*/
        const contentWidth = !useVirtualization && this.props.customFooterContent && !this.state.isWrapped
            ? this.state.contentWidth : null;

        // when virtualized, we need to recalculate item size when columns change,
        // => remount here by using columns as key instead of finding a way how to propagate the columns into SortableList
        const columnsKey = useVirtualization ? this.props.columns.map(col => col.id).join("-") : "";

        return (
            <StyledSmartFastEntryList
                showLineNumbers={this.props.showLineNumbers}
                onKeyDown={this.handleKeyDown}
                style={this.props.style}
                className={this.props.className}
                data-testid={TestIds.FastEntryListVirtualization}>
                {useVirtualization &&
                    <SearchField
                        ref={this._searchFieldRef}
                        onChange={this.handleSearchChange}
                        isLight={this.props.isLight}/>}
                {useVirtualization && this.renderErrorStepper()}
                <FastEntryList
                    id={this.props.bindingContext.toString()}
                    recalculate={columnsKey}
                    useLabelWrapping={this.props.useLabelWrapping}
                    onWrapChange={this.handleWrapChange}
                    isInAuditTrail={this.props.storage.formMode === FormMode.AuditTrail}
                    showLineNumbers={this.props.showLineNumbers}
                    isCollapsed={!this.props.storage.getGroupStatus(this.props.groupId)?.isExpanded}
                    onCollapsingEnd={this.handleCollapsingEnd}
                    onCollapsed={this.handleCollapsed}
                    canReorder={!!this.canReorder}
                    onReorder={this.handleReorder}
                    isCollapsible={this.props.isCollapsible}
                    // when filter is used, we still want to show SearchField here, even if there are less items than the threshold,
                    // but FastEntryList should not be virtualized so that its height isn't always 70vh,
                    // even if there are only few items
                    // => call useVirtualization with applySearchValue true
                    useVirtualization={this.useVirtualization(true)}
                    height={"70vh"}
                    containsPrimary={this.props.containsPrimary}
                    customPreContent={!!this.props.customAction ? this.renderMainToggle() : null}
                    labels={this.getLabels()}
                    labelsLeftPad={labelsLeftPad}
                    passRef={this._fastEntryListRef}
                    virtualizedListRef={this._virtualizedListRef}
                >

                    {this.getFastEntryListChildren(sortedItems, isFormDisabled)}

                </FastEntryList>
                {!this.props.customAction && !this.isComparison() && (canAdd || this.props.customActionButtons || this.props.customFooterContent) && (
                    <ButtonsFooter width={contentWidth}
                                   data-testid={TestIds.ButtonsFooter}
                                   hasMargin={!!this.canReorder}>
                        {this.props.customActionButtons && this.renderCustomActionButtons(ActionButtonPosition.Before)}
                        {canAdd &&
                            <Button
                                hotspotId={"fastEntryAdd"}
                                isTransparent={this.props.isAddButtonTransparent}
                                onClick={this.handleAdd}
                                passRef={this._buttonRef}
                                isDisabled={isFormDisabled || this.isAddDisabled}
                                hoverAlert={this.props.addDisabledAlert}>
                                {this.props.customAddButtonText ?? this.props.t("Common:General.Add")}
                            </Button>
                        }
                        {this.props.customActionButtons && this.renderCustomActionButtons(ActionButtonPosition.After)}
                        {this.props.customFooterContent}
                    </ButtonsFooter>
                )}
                {this.props.customAction && this.renderConfirmationButtons()}

            </StyledSmartFastEntryList>
        );
    };
}

export default withDomManipulator(withTranslation("Common")(SmartFastEntryList));
